import { apiClient } from './client';
import type {
  StatusAntwort,
  BrandingAntwort,
  Bestellung,
  BestellungenSummary,
  Eskalation,
  EskalationenSummary,
  EskalationsUpdate,
  AgentAktion,
  AktionenSummary,
  Unklarheit,
  MessberichteAntwort,
  AgentSteuerungAntwort,
  EskalationsHistorienEintrag,
  FehlendeAbEintrag,
  LieferantScore,
  LieferantenSummary,
  PaginationQuery,
  PaginierteAntwort,
  Rechnung,
  RechnungenSummary,
  RechnungUpdate,
  SkontoUebersicht,
  RfqVorgangListe,
  RfqVorgaengeSummary,
  RfqDetail,
  RfqAnlegen,
  RfqUpdate,
  Tagesbriefing,
  VesselTracking,
  WinFormTestAntwort,
  WinFormViewZeile,
} from './types';
import type {
  BenutzerSeitenrechte,
  BenutzerSeitenrechteUpdate,
  Benutzer,
  BenutzerAktualisieren,
  BenutzerErstellen,
  LoginRequest,
  PasswortAendern,
  SeitenDefinition,
  TokenAntwort,
} from './auth-types';
import { paginationParamsZuApiParams } from './pagination';

async function paginierteListeAbrufen<T, Summary = Record<string, unknown>>(
  pfad: string,
  query?: PaginationQuery,
  extraParams?: Record<string, unknown>,
) {
  const { data } = await apiClient.get<PaginierteAntwort<T, Summary>>(pfad, {
    params: {
      ...paginationParamsZuApiParams(query),
      ...(extraParams ?? {}),
    },
  });
  return data;
}

async function alleSeitenAbrufen<T>(
  pfad: string,
  extraParams?: Record<string, unknown>,
) {
  const ersteSeite = await paginierteListeAbrufen<T>(
    pfad,
    { page: 1, pageSize: 200 },
    extraParams,
  );

  if (ersteSeite.pages <= 1) {
    return ersteSeite.items;
  }

  const items = [...ersteSeite.items];
  for (let seite = 2; seite <= ersteSeite.pages; seite += 1) {
    const weitereSeite = await paginierteListeAbrufen<T>(
      pfad,
      { page: seite, pageSize: 200 },
      extraParams,
    );
    items.push(...weitereSeite.items);
  }

  return items;
}

export const api = {
  // ─── Auth (Lastenheft 5.3) ──────────────────────────────────────────────
  login: (daten: LoginRequest) =>
    apiClient.post<TokenAntwort>('/auth/login', daten).then((r) => r.data),

  loginMicrosoft: (microsoftToken: string) =>
    apiClient
      .post<TokenAntwort>('/auth/login/microsoft', { microsoft_token: microsoftToken })
      .then((r) => r.data),

  getMe: () =>
    apiClient.get<Benutzer>('/auth/me').then((r) => r.data),

  getPages: () =>
    apiClient.get<SeitenDefinition[]>('/pages').then((r) => r.data),

  passwortAendern: (daten: PasswortAendern) =>
    apiClient.put('/auth/passwort', daten).then((r) => r.data),

  // ─── Admin: Benutzerverwaltung ──────────────────────────────────────────
  getBenutzer: () =>
    apiClient.get<Benutzer[]>('/admin/benutzer').then((r) => r.data),

  benutzerErstellen: (daten: BenutzerErstellen) =>
    apiClient.post<Benutzer>('/admin/benutzer', daten).then((r) => r.data),

  benutzerAktualisieren: (id: string, daten: BenutzerAktualisieren) =>
    apiClient.patch<Benutzer>(`/admin/benutzer/${id}`, daten).then((r) => r.data),

  benutzerPasswortAendern: (id: string, daten: PasswortAendern) =>
    apiClient.put(`/admin/benutzer/${id}/passwort`, daten).then((r) => r.data),

  benutzerLoeschen: (id: string) =>
    apiClient.delete(`/admin/benutzer/${id}`).then((r) => r.data),

  getBenutzerSeitenrechte: (id: string) =>
    apiClient
      .get<BenutzerSeitenrechte>(`/admin/benutzer/${id}/seitenrechte`)
      .then((r) => r.data),

  benutzerSeitenrechteSetzen: (id: string, daten: BenutzerSeitenrechteUpdate) =>
    apiClient
      .post<BenutzerSeitenrechte>(`/admin/benutzer/${id}/seitenrechte`, daten)
      .then((r) => r.data),

  // ─── Status ─────────────────────────────────────────────────────────────
  getStatus: () =>
    apiClient.get<StatusAntwort>('/status').then((r) => r.data),

  // ─── Branding (öffentlich, kein Token nötig) ───────────────────────────
  getBranding: () =>
    apiClient.get<BrandingAntwort>('/branding').then((r) => r.data),

  // ─── Abgleich-Toleranzen (Admin) ────────────────────────────────────────
  patchToleranzen: (aenderungen: Record<string, number>) =>
    apiClient
      .patch<{ toleranzen: Record<string, number>; geaendert: Record<string, number> }>(
        '/admin/konfiguration/toleranzen',
        aenderungen,
      )
      .then((r) => r.data),

  resetToleranzen: () =>
    apiClient
      .delete<{ toleranzen: Record<string, number> }>('/admin/konfiguration/toleranzen')
      .then((r) => r.data),

  // ─── Bestellungen ───────────────────────────────────────────────────────
  getBestellungen: () =>
    alleSeitenAbrufen<Bestellung>('/bestellungen'),

  getBestellungenSeite: (query?: PaginationQuery) =>
    paginierteListeAbrufen<Bestellung, BestellungenSummary>('/bestellungen', query),

  getFehlendeAbs: () =>
    apiClient.get<FehlendeAbEintrag[]>('/fehlende-abs').then((r) => r.data),

  // ─── Eskalationen ──────────────────────────────────────────────────────
  getEskalationen: (nurOffen = false) =>
    alleSeitenAbrufen<Eskalation>(
      '/eskalationen',
      nurOffen ? { nur_offen: true } : undefined,
    ),

  getEskalationenSeite: (query?: PaginationQuery, nurOffen = false) =>
    paginierteListeAbrufen<Eskalation, EskalationenSummary>(
      '/eskalationen',
      query,
      nurOffen ? { nur_offen: true } : undefined,
    ),

  getEskalationsHistorie: (bestellungId: string) =>
    apiClient
      .get<EskalationsHistorienEintrag[]>(
        `/bestellungen/${bestellungId}/eskalationshistorie`,
      )
      .then((r) => r.data),

  patchEskalation: (id: string, update: EskalationsUpdate) =>
    apiClient.patch(`/eskalationen/${id}`, update).then((r) => r.data),

  // ─── Rechnungsprüfung / 3-Way-Match (optionales Zusatzmodul) ────────────
  getRechnungenSeite: (query?: PaginationQuery) =>
    paginierteListeAbrufen<Rechnung, RechnungenSummary>('/rechnungen', query),

  getRechnung: (rechnungId: string) =>
    apiClient.get<Rechnung>(`/rechnungen/${rechnungId}`).then((r) => r.data),

  patchRechnung: (id: string, update: RechnungUpdate) =>
    apiClient.patch(`/rechnungen/${id}`, update).then((r) => r.data),

  getSkontoUebersicht: () =>
    apiClient.get<SkontoUebersicht>('/skonto').then((r) => r.data),

  patchRechnungSkontoGenutzt: (rechnungId: string) =>
    apiClient.patch(`/skonto/${rechnungId}/genutzt`).then((r) => r.data),

  // ─── RFQ-Angebotsvergleich (optionales Zusatzmodul) ─────────────────────
  getRfqSeite: (query?: PaginationQuery) =>
    paginierteListeAbrufen<RfqVorgangListe, RfqVorgaengeSummary>('/rfq', query),

  getRfq: (rfqId: string) =>
    apiClient.get<RfqDetail>(`/rfq/${rfqId}`).then((r) => r.data),

  rfqAnlegen: (daten: RfqAnlegen) =>
    apiClient.post<{ rfq_id: string; referenz_nummer: string }>('/rfq', daten).then((r) => r.data),

  angebotHochladen: (rfqId: string, datei: File) => {
    const formData = new FormData();
    formData.append('datei', datei);
    // Content-Type bewusst auf null: entfernt den globalen application/json-Default,
    // sodass der Browser multipart/form-data inkl. korrekter boundary setzt
    // (sonst kommt das Feld 'datei' nicht im Backend an -> 422 "Field required").
    return apiClient
      .post(`/rfq/${rfqId}/angebote`, formData, {
        headers: { 'Content-Type': null },
      })
      .then((r) => r.data);
  },

  patchRfq: (rfqId: string, update: RfqUpdate) =>
    apiClient.patch(`/rfq/${rfqId}`, update).then((r) => r.data),

  // ─── Tagesbriefing (optionales Zusatzmodul, read-only) ──────────────────
  getTagesbriefing: (zeitraum?: string) =>
    apiClient
      .get<Tagesbriefing>('/tagesbriefing', {
        params: zeitraum ? { zeitraum } : {},
      })
      .then((r) => r.data),

  // ─── Aktionen ──────────────────────────────────────────────────────────
  getAktionen: (typ?: string) =>
    alleSeitenAbrufen<AgentAktion>('/aktionen', typ ? { typ } : undefined),

  getAktionenSeite: (query?: PaginationQuery, typ?: string) =>
    paginierteListeAbrufen<AgentAktion, AktionenSummary>(
      '/aktionen',
      query,
      typ ? { typ } : undefined,
    ),

  // ─── Messberichte ──────────────────────────────────────────────────────
  getMessberichte: () =>
    apiClient.get<MessberichteAntwort>('/messberichte').then((r) => r.data),

  // ─── Lieferanten ───────────────────────────────────────────────────────
  getLieferanten: () =>
    alleSeitenAbrufen<LieferantScore>('/lieferanten'),

  getLieferantenSeite: (query?: PaginationQuery) =>
    paginierteListeAbrufen<LieferantScore, LieferantenSummary>('/lieferanten', query),

  // ─── Vessel Tracking (Lastenheft 4.5) ──────────────────────────────────
  getVesselTracking: () =>
    apiClient.get<VesselTracking[]>('/vessel-tracking').then((r) => r.data),

  getVesselTrackingAktiv: () =>
    apiClient.get<VesselTracking[]>('/vessel-tracking/aktiv').then((r) => r.data),

  getVesselTrackingFuerBestellung: (bestellungId: string) =>
    apiClient
      .get<VesselTracking>(`/vessel-tracking/${bestellungId}`)
      .then((r) => r.data),

  // ─── Unklarheiten ──────────────────────────────────────────────────────
  getUnklarheiten: () =>
    apiClient.get<Unklarheit[]>('/unklarheiten').then((r) => r.data),

  // ─── Logs ──────────────────────────────────────────────────────────────
  getLogs: (anzahl = 30) =>
    apiClient
      .get<string[] | { zeilen: string[] }>('/logs', { params: { anzahl } })
      .then((r) => {
        const data = r.data;
        return Array.isArray(data) ? data : data.zeilen ?? [];
      }),

  // ─── Agent-Steuerung ──────────────────────────────────────────────────
  agentStarten: () =>
    apiClient
      .post<AgentSteuerungAntwort>('/agent/start')
      .then((r) => r.data),

  agentStoppen: () =>
    apiClient
      .post<AgentSteuerungAntwort>('/agent/stop')
      .then((r) => r.data),

  agentZyklus: () =>
    apiClient
      .post<AgentSteuerungAntwort>('/agent/zyklus', undefined, { timeout: 600_000 })
      .then((r) => r.data),

  agentLieferterminAnfragen: () =>
    apiClient
      .post<{ status: string; anfragen_gesendet: number; meldung: string }>(
        '/agent/liefertermin-anfragen',
      )
      .then((r) => r.data),

  agentNeustart: () =>
    apiClient
      .post<AgentSteuerungAntwort>('/agent/neustart')
      .then((r) => r.data),

  // ─── WinForm / ERP ────────────────────────────────────────────────────
  winformTest: () =>
    apiClient
      .get<WinFormTestAntwort>('/admin/winform/test')
      .then((r) => r.data),

  winformView: () =>
    apiClient
      .get<WinFormViewZeile[]>('/admin/winform/view')
      .then((r) => r.data),
};
